import React, { useState } from "react";
import { toast } from "sonner";

/**
 * <CouponApplyField> — the ONE component every checkout surface uses
 * for the "Have a coupon code?" apply/swap/remove UX.
 *
 * Behaviour matches the campaign-fee checkout that shipped first:
 *   1. Collapsed state: a "Have a coupon code?" link.
 *   2. Open state:      inline input + Apply/Cancel buttons.
 *   3. Applied state:   green chip showing the code + savings, with
 *                       a "Swap" affordance to re-open the input.
 *
 * Surfaces don't call the apply endpoint themselves — they pass a
 * `code` string to the parent's onApply callback and let the parent
 * handle the network call (each surface has different validation and
 * response semantics).
 *
 * Props:
 *   applied        {code,type,value,discount} | null — current applied coupon
 *   onApply(code)  async — called with the trimmed uppercase code. Should
 *                          throw with e.response.data.detail.{message,code}
 *                          on failure so we can surface the exact reason.
 *   onRemove()     async — called when the vendor removes the coupon
 *                          (optional; omit to hide the "Remove" affordance)
 *   testIdPrefix   string — prefix for data-testid (default "coupon")
 *   disabled       bool  — parent-imposed disable (e.g. mid-payment)
 */
export default function CouponApplyField({
  applied = null,
  onApply,
  onRemove,
  testIdPrefix = "coupon",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const rupees = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(Number(n || 0));

  const submit = async () => {
    if (busy || disabled) return;
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setBusy(true);
    setErr("");
    try {
      await onApply(trimmed);
      // Parent handles the state update; we just close the input.
      setCode("");
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      setErr(msg || "Couldn't apply this coupon.");
      toast.error(msg || "Couldn't apply this coupon.");
    } finally {
      setBusy(false);
    }
  };

  if (applied) {
    return (
      <div
        className="flex items-center justify-between gap-3 bg-[#e8f5ee] border-2 border-[#176b3a] rounded-lg px-3 py-2"
        data-testid={`${testIdPrefix}-applied`}
      >
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider font-bold text-[#176b3a]">
            Coupon applied
          </p>
          <p className="text-sm font-bold text-black" data-testid={`${testIdPrefix}-applied-code`}>
            {applied.code}
            {applied.type === "percentage"
              ? ` · ${applied.value}% off`
              : ` · ₹${applied.value} off`}
          </p>
          <p className="text-[11px] text-black/70">Saved {rupees(applied.discount)}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setOpen(true);
              setCode("");
              setErr("");
            }}
            className="text-xs font-bold underline underline-offset-2"
            data-testid={`${testIdPrefix}-swap`}
            disabled={disabled}
          >
            Swap
          </button>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="text-xs font-bold text-[#8b1212] underline underline-offset-2"
              data-testid={`${testIdPrefix}-remove`}
              disabled={disabled}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    );
  }

  if (open) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-form`}>
        <div className="flex gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="ENTER COUPON CODE"
            disabled={busy || disabled}
            maxLength={32}
            className="flex-1 h-10 rounded-md border-2 border-black bg-white px-3 uppercase tracking-wider text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[var(--graffiti-yellow)]"
            data-testid={`${testIdPrefix}-input`}
            autoFocus
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy || disabled || !code.trim()}
            className="mkt-btn"
            data-testid={`${testIdPrefix}-apply`}
          >
            {busy ? "Applying…" : "Apply"}
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setErr("");
              setCode("");
            }}
            className="text-xs font-bold underline underline-offset-2"
            data-testid={`${testIdPrefix}-cancel`}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
        {err && (
          <p className="text-xs font-bold text-[#8b1212]" data-testid={`${testIdPrefix}-error`}>
            {err}
          </p>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="text-sm font-bold underline underline-offset-2"
      data-testid={`${testIdPrefix}-open`}
      disabled={disabled}
    >
      Have a coupon code?
    </button>
  );
}
